import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, BookOpen, Loader2, PencilRuler, Settings, Sparkles, Wand2, Undo2, Volume2, Captions } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { TagsField } from '@/components/common/form-fields';
import { Select } from '@/components/ui/Select';
import { courseService } from '@/services/course.service';
import {
  languageOptions,
  getVoiceTypesForLanguage,
  getDefaultVoiceType,
  isVoiceTypeAvailable,
  isVoiceTypePitchSupported,
  getVoicesForLanguageAndType,
  formatVoiceLabel,
  getPreviewText,
  type VoiceType,
} from '@/constants/languageOptions';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { COURSE_CATALOG_PERMISSION } from '@/constants/constant-permissions';
import { ADMIN_COURSE_CATALOG } from '@/constants/constant-routes';

type ManualCourseFormData = {
  title: string;
  description: string;
  targetAudience: string;
  difficultyLevel: 'beginner' | 'intermediate' | 'advanced';
  industry: string;
  languageCode: string;
  contentDeliveryType: 'html' | 'html_with_audio' | 'video_presentation';
  theme: 'educational' | 'modern' | 'corporate' | 'dark' | 'minimal';
  ttsConfig: {
    voiceType: VoiceType;
    voiceName: string;
    speakingRate: number;
    pitch: number;
    volumeGainDb: number;
  };
  captionConfig: {
    enableCaptions: boolean;
  };
};

const difficultyOptions = [
  { label: 'Beginner - No prior knowledge needed', value: 'beginner' },
  { label: 'Intermediate - Some experience helpful', value: 'intermediate' },
  { label: 'Advanced - In-depth understanding required', value: 'advanced' },
];

const deliveryOptions = [
  { label: 'HTML (text lessons)', value: 'html' },
  { label: 'HTML with Audio', value: 'html_with_audio' },
  { label: 'Video Presentation (scenes)', value: 'video_presentation' },
];

// Theme templates with a mini slide preview so the admin sees the actual
// design look before creating the course (mirrors the AI course builder).
const THEME_TEMPLATES = [
  { key: 'educational', label: 'Educational', desc: 'Warm & approachable', color: '#3b82f6', secondary: '#10b981', bg: '#f8fafc', panelBg: '#ffffff', radius: '12px', accentType: 'gradient' },
  { key: 'modern', label: 'Modern', desc: 'Sleek & tech-forward', color: '#8b5cf6', secondary: '#06b6d4', bg: '#faf8ff', panelBg: '#faf8ff', radius: '16px', accentType: 'gradient' },
  { key: 'corporate', label: 'Corporate', desc: 'Professional & structured', color: '#059669', secondary: '#0891b2', bg: '#f9fafb', panelBg: '#f9fafb', radius: '4px', accentType: 'solid' },
  { key: 'dark', label: 'Dark', desc: 'High contrast & bold', color: '#60a5fa', secondary: '#34d399', bg: '#1f2937', panelBg: '#374151', radius: '8px', accentType: 'gradient' },
  { key: 'minimal', label: 'Minimal', desc: 'Clean & typography-driven', color: '#6b7280', secondary: '#9ca3af', bg: '#ffffff', panelBg: 'transparent', radius: '0px', accentType: 'line' },
] as const;

export default function CreateManualCourse() {
  // Page access + action control — reuse the catalog write permission.
  usePermissionRouteAccess([
    COURSE_CATALOG_PERMISSION.COURSE_CATALOG_READ,
    COURSE_CATALOG_PERMISSION.COURSE_CATALOG_WRITE,
  ]);
  const canWrite = useHasPermission([COURSE_CATALOG_PERMISSION.COURSE_CATALOG_WRITE]);

  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);

  // Prerequisites — managed as a chip list outside the form fields.
  const [prerequisites, setPrerequisites] = useState<string[]>([]);

  // AI description enhancement (mirrors the AI course builder).
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [originalDescBeforeEnhance, setOriginalDescBeforeEnhance] = useState<string>('');

  const MIN_DESCRIPTION = 100;

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ManualCourseFormData>({
    mode: 'all',
    defaultValues: {
      title: '',
      description: '',
      targetAudience: '',
      difficultyLevel: 'intermediate',
      industry: '',
      languageCode: 'en-US',
      contentDeliveryType: 'html',
      theme: 'educational',
      ttsConfig: {
        voiceType: getDefaultVoiceType('en-US'),
        voiceName: '',
        speakingRate: 1.0,
        pitch: 0,
        volumeGainDb: 0,
      },
      captionConfig: {
        enableCaptions: true,
      },
    },
  });

  // Live-watched description value for the character count + enhance gating.
  const descriptionValue = watch('description') || '';

  // Voice settings only apply where audio is produced — an html course never
  // generates narration, so the whole block is hidden and never submitted.
  const delivery_type = watch('contentDeliveryType');
  const language_code = watch('languageCode');
  const tts_config = watch('ttsConfig');
  const caption_config = watch('captionConfig');
  const needs_voice = delivery_type !== 'html';
  // Captions are burned into rendered scene videos, so they only apply to the
  // video delivery type.
  const needs_captions = delivery_type === 'video_presentation';

  // Keep the voice type valid for the selected language.
  useEffect(() => {
    if (language_code && tts_config?.voiceType && !isVoiceTypeAvailable(language_code, tts_config.voiceType)) {
      setValue('ttsConfig.voiceType', getDefaultVoiceType(language_code));
    }
  }, [language_code, tts_config?.voiceType, setValue]);

  // Keep the voice name valid for the language + type, and zero the pitch on
  // voice types that don't support it.
  useEffect(() => {
    if (!language_code || !tts_config?.voiceType) return;
    const available_voices = getVoicesForLanguageAndType(language_code, tts_config.voiceType);
    if (!available_voices.some((v) => v.voice_name === tts_config.voiceName)) {
      setValue('ttsConfig.voiceName', available_voices[0]?.voice_name || '');
    }
    if (!isVoiceTypePitchSupported(tts_config.voiceType) && tts_config.pitch !== 0) {
      setValue('ttsConfig.pitch', 0);
    }
  }, [language_code, tts_config?.voiceType, tts_config?.voiceName, tts_config?.pitch, setValue]);

  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const [isPreviewingVoice, setIsPreviewingVoice] = useState(false);

  const handlePreviewVoice = async () => {
    setIsPreviewingVoice(true);
    try {
      const apiClient = (await import('@/services/api')).default;
      const response = await apiClient.post('/api/tts/preview', {
        text: getPreviewText(language_code),
        language_code,
        voice_name: tts_config.voiceName,
        voice_type: tts_config.voiceType,
        speaking_rate: tts_config.speakingRate,
        ...(isVoiceTypePitchSupported(tts_config.voiceType) ? { pitch: tts_config.pitch } : {}),
        volume_gain_db: tts_config.volumeGainDb,
      });
      const audio_data = response.data?.data?.audio_base64;
      if (!audio_data) {
        toast.error('No audio data received');
        return;
      }
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current.currentTime = 0;
      }
      const audio = new Audio(`data:audio/mp3;base64,${audio_data}`);
      previewAudioRef.current = audio;
      await audio.play();
    } catch (error: any) {
      toast.error(error?.response?.data?.description || 'Voice preview failed');
    } finally {
      setIsPreviewingVoice(false);
    }
  };

  // Stop any playing preview when leaving the page.
  useEffect(() => () => previewAudioRef.current?.pause(), []);

  // Enhance the description with AI (same endpoint the AI builder uses).
  const handleEnhanceDescription = async () => {
    const current = (watch('description') || '').trim();
    if (!current || current.length < 10) {
      toast.error('Please enter at least 10 characters to enhance');
      return;
    }
    setIsEnhancing(true);
    const loadingToast = toast.loading('Enhancing description with AI...');
    try {
      const result = await courseService.enhanceUserInput({
        user_course_idea: current,
        targeted_audience: (watch('targetAudience') || '').trim() || undefined,
        difficulty_level: watch('difficultyLevel') || undefined,
      });
      setOriginalDescBeforeEnhance(current);
      setValue('description', result.enhanced_input, { shouldValidate: true, shouldDirty: true });
      toast.dismiss(loadingToast);
      toast.success('Description enhanced successfully!');
    } catch (error: any) {
      toast.dismiss(loadingToast);
      toast.error(
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to enhance description'
      );
    } finally {
      setIsEnhancing(false);
    }
  };

  const onSubmit = handleSubmit(async (data) => {
    if (!canWrite || isCreating) return;

    const title = data.title.trim();
    const description = data.description.trim();
    if (!title) {
      toast.error('Course title is required');
      return;
    }
    if (!description) {
      toast.error('Course description is required');
      return;
    }

    setIsCreating(true);
    try {
      const result = await courseService.createManualCourse({
        title,
        description,
        target_audience: data.targetAudience.trim() || undefined,
        difficulty_level: data.difficultyLevel,
        prerequisites: prerequisites.length > 0 ? prerequisites : undefined,
        industry: data.industry.trim() || undefined,
        language_code: data.languageCode,
        content_delivery_type: data.contentDeliveryType,
        theme: data.theme,
        // Voice is only meaningful where audio gets generated. The backend also
        // drops it for html, but don't send it in the first place.
        ...(data.contentDeliveryType !== 'html' && {
          tts_config: {
            voice_name: data.ttsConfig.voiceName || undefined,
            voice_type: data.ttsConfig.voiceType,
            speaking_rate: data.ttsConfig.speakingRate,
            ...(isVoiceTypePitchSupported(data.ttsConfig.voiceType) ? { pitch: data.ttsConfig.pitch } : {}),
            volume_gain_db: data.ttsConfig.volumeGainDb,
          },
        }),
        // Captions only exist on rendered scene videos.
        ...(data.contentDeliveryType === 'video_presentation' && {
          caption_config: { enable_captions: data.captionConfig.enableCaptions },
        }),
        // No estimated_duration: the course total is rolled up from the scene /
        // content-item durations added while authoring.
      });

      toast.success('Course created — add your modules and content now.');
      // Land on the Edit Course flow so the admin builds the structure manually.
      navigate(`/admin/edit-course/${result.course_id}`);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to create course');
    } finally {
      setIsCreating(false);
    }
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Back link */}
        <button
          type="button"
          onClick={() => navigate(ADMIN_COURSE_CATALOG)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-3 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to catalog
        </button>

        {/* Compact Hero Section */}
        <div className="text-center mb-6 bg-gradient-to-br from-primary-50 via-white to-purple-50 rounded-xl py-4 -mx-4 sm:mx-0">
          <div className="inline-flex items-center justify-center w-10 h-10 bg-gradient-to-r from-primary-500 to-purple-600 rounded-xl mb-3 shadow-lg">
            <PencilRuler className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent">
            Create Course Manually
          </h1>
          <p className="text-sm text-gray-700 mb-3 max-w-xl mx-auto px-4">
            Set up the course details, then build the modules and content yourself.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
              <span>No AI generation</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
              <span>You control the structure</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 bg-purple-500 rounded-full" />
              <span>Edit anytime</span>
            </div>
          </div>
        </div>

        {/* Main Form */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 max-w-4xl mx-auto">
          <form onSubmit={onSubmit}>
            {/* Course Details */}
            <div className="p-4 sm:p-6 lg:p-8 space-y-5">
              <div>
                <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-2">
                  Course Details
                </label>
                <p className="text-xs sm:text-sm text-gray-600">
                  These describe the course. You'll add modules, content items and scenes after it's created.
                </p>
              </div>

              <Input<ManualCourseFormData>
                name="title"
                control={control}
                label="Title"
                required
                placeholder="e.g. Introduction to TypeScript"
                error={errors.title?.message}
                rules={{ required: 'Title is required' }}
              />

              {/* Description with AI enhance + character count */}
              <div className="w-full">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Description<span className="text-red-500 ml-1">*</span>
                    <span className="ml-2 font-normal text-xs text-gray-500">(Minimum {MIN_DESCRIPTION} characters)</span>
                  </label>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {originalDescBeforeEnhance && (
                      <button
                        type="button"
                        onClick={() => {
                          setValue('description', originalDescBeforeEnhance, { shouldValidate: true });
                          setOriginalDescBeforeEnhance('');
                          toast.success('Reverted to original text', { icon: '↶', duration: 2000 });
                        }}
                        className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 transition-all"
                        title="Undo enhancement"
                      >
                        <Undo2 className="w-4 h-4 text-gray-600" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleEnhanceDescription}
                      disabled={isEnhancing || descriptionValue.trim().length < 10}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-xs font-medium"
                    >
                      {isEnhancing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                      {isEnhancing ? 'Enhancing…' : 'Enhance with AI'}
                    </button>
                  </div>
                </div>
                {/* The shared Textarea adds the clean-text check and shows its error. */}
                <Textarea<ManualCourseFormData>
                  name="description"
                  control={control}
                  rules={{
                    required: 'Description is required',
                    minLength: { value: MIN_DESCRIPTION, message: `Description must be at least ${MIN_DESCRIPTION} characters` },
                  }}
                  rows={5}
                  maxLength={1000}
                  placeholder="Describe the course — topics, what learners will achieve, and who it's for."
                  unstyled
                  className={`w-full px-4 py-3 pb-9 text-sm border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none ${
                    errors.description ? 'border-red-500 focus:ring-red-500' : 'border-gray-200'
                  }`}
                  overlay={
                    <div className="absolute bottom-2.5 right-3 text-xs bg-white px-2 py-0.5 rounded-md border border-gray-200">
                      <span className={descriptionValue.length < MIN_DESCRIPTION ? 'text-red-500 font-medium' : 'text-gray-500'}>
                        {descriptionValue.length}
                      </span>
                      <span className="text-gray-500">/1000</span>
                      {descriptionValue.length < MIN_DESCRIPTION && (
                        <span className="text-red-500 ml-1 text-[10px]">({MIN_DESCRIPTION - descriptionValue.length} more)</span>
                      )}
                    </div>
                  }
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <Input<ManualCourseFormData>
                  name="targetAudience"
                  control={control}
                  label="Target Audience"
                  placeholder="e.g. Beginner JavaScript developers"
                />
                <Input<ManualCourseFormData>
                  name="industry"
                  control={control}
                  label="Target Industry"
                  placeholder="e.g. Software Development"
                />
              </div>

              {/* Prerequisites - the shared TagsField adds the clean-text check to each entry. */}
              <TagsField
                label="Prerequisites"
                value={prerequisites}
                onChange={setPrerequisites}
                placeholder="Type a prerequisite and press Enter"
              />
            </div>

            {/* Settings area */}
            <div className="px-4 sm:px-8 pb-4 sm:pb-8 pt-4 space-y-4 sm:space-y-6 bg-gradient-to-r from-gray-50 to-primary-50 border-t border-gray-100">
              {/* Course Settings */}
              <div className="space-y-4 p-4 bg-white rounded-xl border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-primary-600" />
                  Course Settings
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <Select<ManualCourseFormData>
                    name="difficultyLevel"
                    control={control}
                    label="Difficulty Level"
                    options={difficultyOptions}
                  />
                  <Select<ManualCourseFormData>
                    name="languageCode"
                    control={control}
                    label="Language"
                    isSearchable
                    options={languageOptions}
                  />
                </div>

              </div>

              {/* Delivery & Design */}
              <div className="space-y-4 p-4 bg-white rounded-xl border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Settings className="w-4 h-4 text-primary-600" />
                  Delivery &amp; Design
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <Select<ManualCourseFormData>
                    name="contentDeliveryType"
                    control={control}
                    label="Content Delivery Type"
                    options={deliveryOptions}
                  />
                </div>

                {/* Voice settings — used every time audio is generated for this
                    course (content-item narration and scene audio). */}
                {needs_voice && (
                  <div className="p-3 bg-gray-50 rounded-lg space-y-3 border border-gray-200">
                    <div>
                      <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-2">
                        <Volume2 className="w-3.5 h-3.5 text-primary-600" />
                        Voice Settings
                      </h4>
                      <p className="text-[11px] text-gray-500 mt-1">
                        Applied whenever you generate audio for this course&apos;s{' '}
                        {delivery_type === 'video_presentation' ? 'scenes' : 'lessons'}, in{' '}
                        {languageOptions.find((o) => o.value === language_code)?.label || language_code}.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Select<ManualCourseFormData>
                        name={'ttsConfig.voiceType' as any}
                        control={control}
                        label="Voice Type"
                        isSearchable={false}
                        options={getVoiceTypesForLanguage(language_code).map((vt) => ({
                          value: vt.value,
                          label: vt.description ? `${vt.label} - ${vt.description}` : vt.label,
                        }))}
                      />
                      <Select<ManualCourseFormData>
                        name={'ttsConfig.voiceName' as any}
                        control={control}
                        label="Voice Name"
                        isSearchable
                        placeholder="Select voice..."
                        options={getVoicesForLanguageAndType(language_code, tts_config.voiceType).map((v) => ({
                          value: v.voice_name,
                          label: formatVoiceLabel(v),
                        }))}
                      />
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-gray-600">
                          Speaking Rate: {tts_config.speakingRate}x
                        </label>
                        <input
                          type="range"
                          min="0.25"
                          max="4"
                          step="0.25"
                          value={tts_config.speakingRate}
                          onChange={(e) => setValue('ttsConfig.speakingRate', parseFloat(e.target.value), { shouldDirty: true })}
                          className="w-full"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-gray-600">
                          Pitch:{' '}
                          {!isVoiceTypePitchSupported(tts_config.voiceType)
                            ? 'N/A'
                            : `${tts_config.pitch > 0 ? '+' : ''}${tts_config.pitch}`}
                        </label>
                        <input
                          type="range"
                          min="-20"
                          max="20"
                          step="1"
                          value={isVoiceTypePitchSupported(tts_config.voiceType) ? tts_config.pitch : 0}
                          onChange={(e) => setValue('ttsConfig.pitch', parseInt(e.target.value), { shouldDirty: true })}
                          disabled={!isVoiceTypePitchSupported(tts_config.voiceType)}
                          title={!isVoiceTypePitchSupported(tts_config.voiceType) ? 'Pitch adjustment is not supported for this voice type' : ''}
                          className={`w-full ${!isVoiceTypePitchSupported(tts_config.voiceType) ? 'opacity-40 cursor-not-allowed' : ''}`}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-gray-600">
                          Volume Gain: {tts_config.volumeGainDb > 0 ? '+' : ''}{tts_config.volumeGainDb} dB
                        </label>
                        <input
                          type="range"
                          min="-20"
                          max="16"
                          step="2"
                          value={tts_config.volumeGainDb}
                          onChange={(e) => setValue('ttsConfig.volumeGainDb', parseInt(e.target.value), { shouldDirty: true })}
                          className="w-full"
                        />
                      </div>
                    </div>

                    <div className="pt-2 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={handlePreviewVoice}
                        disabled={isPreviewingVoice || !tts_config.voiceName}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isPreviewingVoice ? <Loader2 className="w-3 h-3 animate-spin" /> : <Volume2 className="w-3 h-3" />}
                        {isPreviewingVoice ? 'Loading…' : 'Preview Voice'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Video captions — burned into each rendered scene video. */}
                {needs_captions && (
                  <div className="p-3 bg-gray-50 rounded-lg space-y-2 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Captions className="w-3.5 h-3.5 text-primary-600" />
                        <h4 className="text-xs font-semibold text-gray-700">Video Captions</h4>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={caption_config.enableCaptions}
                          onChange={(e) => setValue('captionConfig.enableCaptions', e.target.checked, { shouldDirty: true })}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600" />
                      </label>
                    </div>
                    <p className={`text-[10px] leading-relaxed ${caption_config.enableCaptions ? 'text-gray-500' : 'text-gray-400'}`}>
                      ASS karaoke subtitles, timed to each scene&apos;s narration.
                    </p>
                  </div>
                )}

                {/* Design Theme — visual template preview cards */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Design Theme</label>
                  <p className="text-xs text-gray-500">Pick the template your slides and lessons will use.</p>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    {THEME_TEMPLATES.map(({ key, label, desc, color, secondary, bg, panelBg, radius, accentType }) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setValue('theme', key as ManualCourseFormData['theme'], { shouldDirty: true })}
                        className={`flex flex-col items-center gap-1 px-2 py-2 text-xs rounded-lg border transition-all ${
                          watch('theme') === key
                            ? 'bg-purple-50 border-purple-300 text-purple-700 ring-1 ring-purple-300'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {/* Mini slide preview */}
                        <div
                          className="w-full h-10 rounded overflow-hidden relative"
                          style={{ background: bg, border: key === 'dark' ? 'none' : '1px solid #e5e7eb' }}
                        >
                          {/* Accent line */}
                          <div
                            className="absolute top-0 left-0 w-full"
                            style={{
                              height: accentType === 'line' ? 1 : accentType === 'solid' ? 2 : 3,
                              background: accentType === 'gradient' ? `linear-gradient(90deg, ${color}, ${secondary})` : color,
                            }}
                          />
                          {/* Mini panel */}
                          <div
                            className="absolute"
                            style={{
                              top: 8,
                              left: 6,
                              right: 6,
                              bottom: 4,
                              background: panelBg,
                              borderRadius: radius,
                              boxShadow: panelBg === 'transparent' ? 'none' : '0 0 3px rgba(0,0,0,0.06)',
                            }}
                          >
                            <div className="px-1.5 pt-1.5">
                              <div style={{ height: 3, width: '60%', borderRadius: 1, background: key === 'dark' ? '#f9fafb' : '#374151', opacity: 0.7 }} />
                              <div style={{ height: 2, width: '85%', borderRadius: 1, background: '#9ca3af', marginTop: 3, opacity: 0.4 }} />
                              <div style={{ height: 2, width: '70%', borderRadius: 1, background: '#9ca3af', marginTop: 2, opacity: 0.3 }} />
                            </div>
                          </div>
                        </div>
                        <span className="font-medium text-[11px]">{label}</span>
                        <span className="text-[9px] text-gray-400 leading-tight">{desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Section */}
            <div className="px-4 sm:px-8 py-4 sm:py-6 bg-gray-50 border-t border-gray-100 rounded-b-xl sm:rounded-b-2xl flex flex-col sm:flex-row items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => navigate(ADMIN_COURSE_CATALOG)}
                className="order-2 sm:order-1 text-sm text-gray-500 hover:text-gray-800 transition-colors"
                disabled={isCreating}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canWrite || isCreating}
                title={!canWrite ? 'You do not have permission to create courses' : undefined}
                className="relative w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white font-semibold rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl hover:from-primary-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 disabled:transform-none order-1 sm:order-2"
              >
                {isCreating ? (
                  <span className="flex items-center justify-center gap-2 sm:gap-3">
                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                    <span className="text-sm sm:text-base">Creating…</span>
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2 sm:gap-3">
                    <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="text-sm sm:text-base">Create Course</span>
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
